import { cn } from '@/lib/utils';
import { Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { PublishState } from '../types';

interface Props {
  defaultTopic: string;
  state: PublishState;
  onPublish: (topic: string, payload: string, qos: 0 | 1 | 2) => void;
}

export function PublishPanel({ defaultTopic, state, onPublish }: Props) {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState(defaultTopic);
  const [payload, setPayload] = useState('');
  const [qos, setQos] = useState<0 | 1 | 2>(0);

  // Keep the topic field in sync when the selected topic changes upstream.
  useEffect(() => {
    setTopic(defaultTopic);
  }, [defaultTopic]);

  const label =
    state === 'sent'
      ? '✓ Sent'
      : state === 'sending'
        ? 'Sending…'
        : state === 'error'
          ? 'Error'
          : 'PUBLISH';

  return (
    <div className="shrink-0 border-t border-(--color-border) bg-(--color-surface)">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full cursor-pointer items-center gap-2 px-3.5 py-1.5"
      >
        <Send size={13} className="text-(--color-text3)" />
        <span className="flex-1 text-left font-sans text-[0.769em] font-bold text-(--color-text2)">
          Publish
        </span>
        <span className={cn('text-(--color-text3) transition-transform', open && 'rotate-180')}>
          ▾
        </span>
      </button>
      {open && (
        <div className="px-3.5 py-2.5">
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            aria-label="Publish topic"
            className="mb-1.5 w-full rounded border border-(--color-border) bg-(--color-bg) px-2 py-[5px] font-mono text-[0.923em] text-(--color-text) outline-none focus:border-(--color-green)"
          />
          <textarea
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            placeholder='{ "message_type": "...", "payload": {} }'
            aria-label="Publish payload"
            className="mb-1.5 h-[70px] w-full resize-y rounded border border-(--color-border) bg-(--color-bg) px-2 py-[5px] font-mono text-[0.769em] text-(--color-text) outline-none focus:border-(--color-green)"
          />
          <div className="flex items-center gap-2">
            <span className="font-sans text-[0.615em] text-(--color-text3)">QoS</span>
            <select
              value={qos}
              onChange={(e) => setQos(Number(e.target.value) as 0 | 1 | 2)}
              className="rounded border border-(--color-border) bg-(--color-bg) px-2 py-1 font-mono text-[0.769em] text-(--color-text2) outline-none"
            >
              <option value={0}>0 — At most once</option>
              <option value={1}>1 — At least once</option>
              <option value={2}>2 — Exactly once</option>
            </select>
            <button
              type="button"
              disabled={state === 'sending'}
              onClick={() => onPublish(topic, payload, qos)}
              className="ml-auto flex h-7 items-center gap-1.5 rounded-md bg-(--color-green) px-4 font-sans text-[0.769em] font-bold text-(--color-on-accent) disabled:opacity-60"
            >
              <Send size={11} />
              {label}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
